import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera as CameraIcon, Plus, X } from "lucide-react";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Skeleton } from "@shared/ui/feedback/Skeleton";
import { ErrorState } from "@shared/ui/feedback/ErrorState";
import {
  ActionButton,
  FieldGroup,
  FormActions,
  FormSection,
  FormStack,
  SelectField,
  TextAreaField,
  TextInputField,
} from "@shared/ui/forms/FormFields";
import { platform } from "@shared/lib/platform";
import { toast } from "@shared/ui/feedback/Toast";
import { toApiError } from "@shared/api/errors";
import { HeroHeader } from "@shared/ui/headers/HeroHeader";
import { pageCopy } from "@shared/lib/pageCopy";
import { cn } from "@shared/lib/cn";
import { focusNextOnEnter, applyEnterKeyHint } from "@shared/lib/formNav";
import {
  createClassified,
  currentMemberId,
  defaultClassifiedExpiry,
  deleteClassifiedGalleryImage,
  fetchClassified,
  fetchClassifiedCategories,
  updateClassified,
  uploadClassifiedGalleryImage,
  uploadClassifiedProfileImage,
} from "./api";

type StagedGallery = { file: Blob; preview: string };
type ExistingGallery = { id: string; url: string; kind: "existing" };

/**
 * Create / edit listing — grouped resident-app form layout. Main listing fields
 * live together in one shared card, with photos and buyer visibility handled in
 * dedicated sections.
 */
export function ClassifiedForm(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: editId } = useParams<{ id?: string }>();
  const isEdit = Boolean(editId && editId !== "new");
  const formCopy = pageCopy().classifieds.form;
  const copy = isEdit ? formCopy.edit : formCopy.new;

  const memberId = currentMemberId();

  const categoriesQuery = useQuery({
    queryKey: ["classified-categories"],
    queryFn: fetchClassifiedCategories,
  });

  const existingQuery = useQuery({
    queryKey: ["classified", editId],
    queryFn: () => fetchClassified(editId ?? ""),
    enabled: isEdit && !!editId,
  });

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [expiry, setExpiry] = useState(defaultClassifiedExpiry());
  const [showName, setShowName] = useState(true);
  const [showEmail, setShowEmail] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [sellerEmail, setSellerEmail] = useState("");
  const [sellerPhone, setSellerPhone] = useState("");
  const [profileFile, setProfileFile] = useState<Blob | null>(null);
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [newGallery, setNewGallery] = useState<StagedGallery[]>([]);
  const [existingGallery, setExistingGallery] = useState<ExistingGallery[]>([]);
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);

  useEffect(() => {
    const row = existingQuery.data;
    if (!row) return;
    setTitle(row.title);
    setDescription(row.description);
    setPrice(row.price != null ? String(row.price) : "0");
    setCategoryId(row.classified_category_id ?? "");
    setExpiry(row.expiry?.slice(0, 10) ?? defaultClassifiedExpiry());
    setShowName(row.show_name);
    setShowEmail(row.show_email);
    setShowPhone(row.show_phone);
    setSellerEmail(row.seller_email ?? "");
    setSellerPhone(row.seller_phone ?? "");
    setProfilePreview(row.cover_image_url ?? null);
    setExistingGallery(
      row.images
        .filter((img) => img.id !== "profile")
        .map((img) => ({
          id: img.id,
          url: img.url,
          kind: "existing" as const,
        })),
    );
  }, [existingQuery.data]);

  const pickImageBlob = async (): Promise<Blob | null> => {
    if (platform.isNative()) {
      const shot = await Camera.getPhoto({
        quality: 80,
        resultType: CameraResultType.Uri,
        source: CameraSource.Prompt,
      });
      if (!shot.webPath) return null;
      return fetch(shot.webPath).then((r) => r.blob());
    }
    return new Promise((resolve) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = () => resolve(input.files?.[0] ?? null);
      input.click();
    });
  };

  const onProfilePhoto = async () => {
    setPhotoBusy(true);
    try {
      const blob = await pickImageBlob();
      if (!blob) return;
      if (profilePreview?.startsWith("blob:"))
        URL.revokeObjectURL(profilePreview);
      setProfileFile(blob);
      setProfilePreview(URL.createObjectURL(blob));
    } catch (e) {
      toast.danger(
        "Couldn't add photo",
        e instanceof Error ? e.message : undefined,
      );
    } finally {
      setPhotoBusy(false);
    }
  };

  const onGalleryPhotos = async () => {
    setPhotoBusy(true);
    try {
      const blob = await pickImageBlob();
      if (!blob) return;
      setNewGallery((prev) => [
        ...prev,
        { file: blob, preview: URL.createObjectURL(blob) },
      ]);
    } catch (e) {
      toast.danger(
        "Couldn't add photo",
        e instanceof Error ? e.message : undefined,
      );
    } finally {
      setPhotoBusy(false);
    }
  };

  const removeExistingGallery = async (imageId: string) => {
    if (!editId) return;
    try {
      await deleteClassifiedGalleryImage(editId, imageId);
      setExistingGallery((prev) => prev.filter((img) => img.id !== imageId));
    } catch (e) {
      toast.danger("Couldn't remove", toApiError(e).message);
    }
  };

  const submit = async () => {
    if (!title.trim()) {
      toast.danger("Title required", "Give your listing a short name.");
      return;
    }
    const priceNum = Number(price);
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      toast.danger("Invalid price", "Enter a valid amount (0 for free).");
      return;
    }
    if (!profilePreview) {
      toast.danger("Cover photo required", "Add a cover photo for your listing.");
      return;
    }
    if (!isEdit && !memberId) {
      toast.danger(
        "Profile required",
        "Your account must be linked to a member profile.",
      );
      return;
    }

    setSaving(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || null,
        classified_category_id: categoryId || null,
        price: priceNum,
        expiry,
        show_name: showName,
        show_email: showEmail,
        show_phone: showPhone,
        seller_phone: sellerPhone.trim() || null,
        seller_email: sellerEmail.trim() || null,
      };

      const classified =
        isEdit && editId
          ? await updateClassified(editId, body)
          : await createClassified({ ...body, member_id: memberId! });

      const classifiedId = classified.classified_id;

      if (profileFile) {
        try {
          await uploadClassifiedProfileImage(classifiedId, profileFile);
        } catch {
          toast.danger(
            "Cover not uploaded",
            "Listing saved but the cover photo could not be uploaded. Please try again.",
          );
          return;
        }
      }

      for (const item of newGallery) {
        try {
          await uploadClassifiedGalleryImage(classifiedId, item.file);
        } catch {
          // Continue uploading the remaining gallery items.
        }
      }

      void queryClient.invalidateQueries({ queryKey: ["classifieds"] });
      void queryClient.invalidateQueries({
        queryKey: ["classified", classifiedId],
      });
      toast.success(isEdit ? "Listing updated" : "Listing posted");
      navigate(`/app/classifieds/${classifiedId}`, { replace: true });
    } catch (e) {
      toast.danger("Could not save", toApiError(e).message);
    } finally {
      setSaving(false);
    }
  };

  if (isEdit && existingQuery.isLoading) {
    return (
      <div className="min-h-full bg-paper px-5 pt-safe-or-6">
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="mt-4 h-24 rounded-2xl" />
      </div>
    );
  }

  if (isEdit && existingQuery.isError) {
    return (
      <div className="min-h-full bg-paper px-5 pt-safe-or-6">
        <ErrorState onRetry={() => existingQuery.refetch()} />
      </div>
    );
  }

  const categories = categoriesQuery.data ?? [];
  const canSubmit =
    title.trim().length > 0 &&
    !!profilePreview &&
    (isEdit || !!memberId);
  const sectionTitleClassName =
    "text-label font-bold normal-case tracking-normal text-ink";

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        backTo={
          isEdit && editId ? `/app/classifieds/${editId}` : "/app/classifieds"
        }
        backBehavior="parent"
        eyebrow={copy.eyebrow}
        title={copy.title}
        subtitle={copy.subtitle}
        titleClassName="whitespace-pre-line leading-tight"
      />

      <FormStack className="pt-5">
        <div
          className="flex flex-col gap-5"
          onKeyDown={focusNextOnEnter}
          onFocus={applyEnterKeyHint}
        >
          <FormSection
            title="Photos"
            titleClassName={sectionTitleClassName}
            description="Add a strong cover photo first, then any optional gallery images."
          >
            <FieldGroup
              label={
                <>
                  Cover photo
                  <span className="ml-0.5 text-danger">*</span>
                </>
              }
            >
              {profilePreview ? (
                <ul className="flex gap-2 overflow-x-auto py-2">
                  <li className="relative shrink-0">
                    <img
                      src={profilePreview}
                      alt=""
                      className="h-20 w-20 rounded-md object-cover hairline"
                    />
                    <button
                      type="button"
                      aria-label="Remove"
                      onClick={() => {
                        if (profilePreview?.startsWith("blob:"))
                          URL.revokeObjectURL(profilePreview);
                        setProfilePreview(
                          existingQuery.data?.cover_image_url ?? null,
                        );
                        setProfileFile(null);
                      }}
                      className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-pill bg-danger text-white"
                    >
                      <X className="h-3 w-3" strokeWidth={2} />
                    </button>
                  </li>
                </ul>
              ) : (
                <button
                  type="button"
                  disabled={photoBusy}
                  onClick={() => void onProfilePhoto()}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-hairline-strong bg-elevated py-3.5 text-body-sm font-medium text-ink-secondary"
                >
                  <CameraIcon className="h-4.5 w-4.5" strokeWidth={1.5} />
                  {photoBusy ? "Adding…" : "Add cover photo"}
                </button>
              )}
            </FieldGroup>

            <FieldGroup
              label="Gallery photos"
              >
              {existingGallery.length > 0 || newGallery.length > 0 ? (
                <ul className="flex gap-2 overflow-x-auto py-2">
                  {existingGallery.map((img) => (
                    <li key={img.id} className="relative shrink-0">
                      <img
                        src={img.url}
                        alt=""
                        className="h-20 w-20 rounded-md object-cover hairline"
                      />
                      <button
                        type="button"
                        aria-label="Remove"
                        onClick={() => void removeExistingGallery(img.id)}
                        className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-pill bg-danger text-white"
                      >
                        <X className="h-3 w-3" strokeWidth={2} />
                      </button>
                    </li>
                  ))}
                  {newGallery.map((img, index) => (
                    <li key={img.preview} className="relative shrink-0">
                      <img
                        src={img.preview}
                        alt=""
                        className="h-20 w-20 rounded-md object-cover hairline"
                      />
                      <button
                        type="button"
                        aria-label="Remove"
                        onClick={() => {
                          URL.revokeObjectURL(img.preview);
                          setNewGallery((prev) =>
                            prev.filter((_, i) => i !== index),
                          );
                        }}
                        className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-pill bg-danger text-white"
                      >
                        <X className="h-3 w-3" strokeWidth={2} />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}

              <button
                type="button"
                disabled={photoBusy}
                onClick={() => void onGalleryPhotos()}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-hairline-strong bg-elevated py-3 text-body-sm font-medium text-ink-secondary"
              >
                <Plus className="h-4 w-4" strokeWidth={1.75} />
                Add gallery photos
              </button>
            </FieldGroup>
          </FormSection>

          <FormSection
            title="Listing details"
            titleClassName={sectionTitleClassName}
            description="Add the main information buyers need before they enquire."
          >
            <TextInputField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Mountain bike — barely used"
              required
            />

            {categoriesQuery.isLoading ? (
              <FieldGroup label="Category">
                <Skeleton className="h-9 w-full" />
              </FieldGroup>
            ) : categories.length === 0 ? (
              <FieldGroup label="Category">
                <p className="text-body-sm text-ink-muted">
                  No categories configured yet.
                </p>
              </FieldGroup>
            ) : (
              <SelectField
                label="Category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                optional
              >
                <option value="">Choose category</option>
                {categories.map((cat) => (
                  <option
                    key={cat.classified_category_id}
                    value={cat.classified_category_id}
                  >
                    {cat.name}
                  </option>
                ))}
              </SelectField>
            )}

            <TextAreaField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              placeholder="Describe the item, condition, and collection details."
              optional
            />

            <TextInputField
              label="Price"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              placeholder="0 for free"
              startAdornment={<span className="font-medium">R</span>}
              required
            />

            <TextInputField
              label="Listing expires"
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              required
            />
          </FormSection>

          <FormSection
            title="Buyer visibility"
            titleClassName={sectionTitleClassName}
            description="Choose which contact details buyers can see when they respond."
          >
            <FieldGroup label="Show buyers">
              <div className="flex flex-wrap gap-2">
                <TogglePill
                  label="Name"
                  checked={showName}
                  onChange={setShowName}
                />
                <TogglePill
                  label="Email"
                  checked={showEmail}
                  onChange={setShowEmail}
                />
                <TogglePill
                  label="Phone"
                  checked={showPhone}
                  onChange={setShowPhone}
                />
              </div>
            </FieldGroup>

            {showEmail ? (
              <TextInputField
                label="Contact email"
                type="email"
                value={sellerEmail}
                onChange={(e) => setSellerEmail(e.target.value)}
                placeholder="Optional — shown if email is enabled"
                optional
              />
            ) : null}

            {showPhone ? (
              <TextInputField
                label="Contact phone"
                type="tel"
                inputMode="tel"
                value={sellerPhone}
                onChange={(e) => setSellerPhone(e.target.value)}
                placeholder="Optional — shown if phone is enabled"
                optional
              />
            ) : null}
          </FormSection>

          <FormActions sticky>
            <ActionButton
              disabled={!canSubmit}
              loading={saving}
              onClick={() => void submit()}
            >
              {isEdit ? "Save changes" : "Post listing"}
            </ActionButton>
          </FormActions>
        </div>
      </FormStack>
    </div>
  );
}

function TogglePill({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={cn(
        "rounded-pill border px-3.5 py-1.5 text-body-sm font-medium transition-colors",
        checked
          ? "border-primary-600 bg-primary-700 text-white"
          : "border-hairline-strong bg-elevated text-ink",
      )}
    >
      {label}
    </button>
  );
}
